import z from 'zod';
import { AuditSchema, Currencies } from '..';
import { PaymentSnapshotSchema } from './payment-method-schema';

export const SaleStatusSchema = z.enum(['paid', 'pending', 'verifying']);
export type SaleStatus = z.infer<typeof SaleStatusSchema>;

// --- 1. ITEMS DE VENTA (Con soporte Multimoneda) ---
export const SaleItemSchema = z.object({
  serviceId: z.string().nullable().default(null),
  courseId: z.string().nullable().default(null),
  name: z.string(),
  is_custom: z.boolean().default(false),
  description: z.string().optional(),

  // Lo que el cliente vio
  price: z.number(),
  currency: Currencies.default('USD'),

  // 🛡️ El valor normalizado para balances y comisiones
  price_accounting_ref: z.number(),

  quantity: z.number().default(1),

  // Costos de material (Snapshot)
  material_cost_applied: z.number(),
  material_cost_currency: Currencies.default('USD'),

  commission_percentage_applied: z.number(),
  commission_amount_accounting: z.number(), // Reemplaza usd
});

export type SaleItem = z.infer<typeof SaleItemSchema>;

// --- 2. ESQUEMA PRINCIPAL DE VENTA ---
export const SaleSchema = z.object({
  professionalId: z.string().nullable(),
  businessId: z.string().nullable().optional(),
  client: z.object({
    uid: z.string().nullable(),
    name: z.string(),
    phone: z.string(),
  }),
  appointmentId: z.string().nullable().optional(),

  items: z.array(SaleItemSchema).min(1, 'Debe haber al menos un servicio'),

  // 💰 HISTORIAL DE ABONOS (Snapshots inmutables)
  payments: z.array(PaymentSnapshotSchema).default([]),

  // --- FINANZAS CONSOLIDADAS (Agnósticas a la moneda) ---
  accounting_currency: Currencies.default('USD'), // 👈 USD o EUR según el salón
  country_currency: Currencies.default('BS'), // 👈 USD o EUR según el salón
  
  total_accounting: z.number(), // Total en la moneda del dueño
  exchange_rate: z.number(), // Tasa oficial del momento del cierre
  revenue_released: z.boolean().default(false),

  amount_paid_accounting: z.number(),
  amount_pending_accounting: z.number(),

  status: SaleStatusSchema.default('pending'),

  // --- RENTABILIDAD (Accounting) ---
  is_commissionable: z.boolean().default(false),
  commission_percentage: z.number(),
  commission_accounting: z.number(),
  total_material_cost_accounting: z.number(),
  business_income_accounting: z.number(),
  commission_status: z.enum(['unpaid', 'paid']).default('unpaid'),

  // --- CHECKOUT (Payment link / Academy proof) ---
  checkout: z.object({
    code: z.string().optional(),
    paymentMethod: z.any().nullable().optional(),
    proofReceivedAt: z.any().nullable().optional(),
    proofFileName: z.string().nullable().optional(),
    proofDataUrl: z.string().nullable().optional(),
    status: z.string().optional(),
    verifiedAt: z.any().nullable().optional(),
    verifiedBy: z.string().nullable().optional(),
    confirmedPaymentMethod: z.any().nullable().optional(),
    manualConfirmation: z.boolean().optional(),
  }).optional(),

  // --- CONTEXT (Product vs Course type hint) ---
  context: z.object({
    type: z.string().optional(),
    courseId: z.string().nullable().optional(),
    id: z.string().nullable().optional(),
  }).optional(),

  // --- ACADEMY ---
  academy: z.object({
    activationLinks: z.array(z.object({
      token: z.string(),
      courseId: z.string(),
      createdAt: z.any(),
    })).optional(),
  }).optional(),

  // --- NOTAS ---
  // Notas del profesional sobre el servicio realizado
  notes: z.string().nullable().optional(),
  // Notas específicas sobre el cliente para esta visita
  client_notes: z.string().nullable().optional(),

  audit: AuditSchema,
});

export type Sale = z.infer<typeof SaleSchema>;

// Este es el tipo que necesita CreateSaleWithPaymentsParams
export type SalePaymentInput = Omit<
  z.infer<typeof PaymentSnapshotSchema>,
  'at'
>;

export type SaleWithId = Sale & { id: string };

export type SaleItemInput = z.infer<typeof SaleItemSchema>;
export type SaleInput = Omit<
  z.infer<typeof SaleSchema>,
  'audit' | 'payments' | 'notes' | 'client_notes'
> & {
  notes?: string;
  client_notes?: string;
  // Permitimos pasar los pagos iniciales si la venta se crea ya cobrada
  payments?: Omit<z.infer<typeof PaymentSnapshotSchema>, 'at'>[];
};
